/* Created by Interactive 63 - https://interactive63.com */
/**
 * Samples the path an element's pivot travels along its position track, for the motion path
 * overlay. Rotation and scale do not move the pivot, so only translate matters: each sample is
 * the pivot's current screen position plus the change in translate carried through the parent's
 * linear screen matrix.
 */
import type { ElementId, Vec2 } from '../../../core/model/types';
import { sampleTrack } from '../../../core/timeline/sample';
import { findTrack } from '../../../core/timeline/keyframes';
import type { Store } from '../../store/store';
import { applyLinear, type Matrix, type Point } from './geometry';
import type { MotionPathData } from './overlay';

export const SAMPLE_INTERVAL_MS = 1000 / 30;
const MAX_SAMPLES = 600;

/** What the motion path needs from the stage. */
export interface MotionSource {
  store: Store;
  node(id: ElementId): SVGGraphicsElement | undefined;
  screenMatrix(node: SVGGraphicsElement): Matrix | null;
  pivotScreen(id: ElementId): Point | null;
  time(): number;
}

/** Whether a value is a two-component vector. */
function isVec2(value: unknown): value is Vec2 {
  return typeof value === 'object' && value !== null && 'x' in value && 'y' in value;
}

/** The motion path for an element with at least two position keyframes, or null. */
export function buildMotionPath(source: MotionSource, id: ElementId): MotionPathData | null {
  const { project } = source.store.getState();
  const track = findTrack(project, id, 'translate');
  if (!track?.enabled || track.keyframes.length < 2) return null;
  const parent = source.node(id)?.parentNode as SVGGraphicsElement | null | undefined;
  const parentMatrix = parent ? source.screenMatrix(parent) : null;
  const pivot = source.pivotScreen(id);
  if (!parentMatrix || !pivot) return null;

  const duration = project.scene.duration;
  const now = sampleTrack(track, source.time(), duration);
  if (!isVec2(now)) return null;

  /** The pivot's screen position when the position is `value`. */
  const at = (value: Vec2): Point => {
    const moved = applyLinear(parentMatrix, { x: value.x - now.x, y: value.y - now.y });
    return { x: pivot.x + moved.x, y: pivot.y + moved.y };
  };

  const step = Math.max(SAMPLE_INTERVAL_MS, duration / MAX_SAMPLES);
  const points: Point[] = [];
  for (let t = 0; t <= duration; t += step) {
    const value = sampleTrack(track, t, duration);
    if (isVec2(value)) points.push(at(value));
  }
  /** The screen point of a handle offset from a keyframe's position. */
  const handleAt = (value: Vec2, handle: Vec2 | undefined): Point | undefined =>
    handle ? at({ x: value.x + handle.x, y: value.y + handle.y }) : undefined;
  const keys = track.keyframes.flatMap((kf) => {
    if (!isVec2(kf.value)) return [];
    const inHandle = handleAt(kf.value, kf.motion?.in);
    const outHandle = handleAt(kf.value, kf.motion?.out);
    return [
      {
        id: kf.id,
        point: at(kf.value),
        ...(inHandle ? { inHandle } : {}),
        ...(outHandle ? { outHandle } : {}),
      },
    ];
  });
  return { elementId: id, points, keys };
}
